import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import '../pages/storefront/CollectionsPage.css';

const SharedCollectionPage = () => {
  const { token } = useParams();
  const [collection, setCollection] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSharedCollection = async () => {
      if (!token) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://revstar-main-platform-production.up.railway.app';
        const response = await fetch(`${backendUrl}/api/collections/share/${token}`);
        const data = await response.json();

        if (data.success) {
          setCollection(data.collection);
          setStore(data.store);
        } else {
          setError(data.error || 'Collection not found');
        }
      } catch (err) {
        console.error('Error fetching shared collection:', err);
        setError('Failed to load collection');
      } finally {
        setLoading(false);
      }
    };

    fetchSharedCollection();
  }, [token]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: store?.currency || 'NGN',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getStoreUrl = () => {
    if (!store?.slug) return null;
    const platformDomain = process.env.REACT_APP_PLATFORM_DOMAIN || 'userevstar.com';
    return `https://${store.slug}.${platformDomain}`;
  };

  if (loading) {
    return (
      <div className="shared-collection-page">
        <div className="collections-loading">
          <div className="loading-spinner"></div>
          <p>Loading collection...</p>
        </div>
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="shared-collection-page">
        <div className="shared-collection-error">
          <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2>Collection Not Found</h2>
          <p>{error || 'This share link may be invalid or the collection has been removed.'}</p>
        </div>
      </div>
    );
  }

  const products = collection.products?.map(cp => cp.product).filter(Boolean) || [];
  const storeUrl = getStoreUrl();

  return (
    <div className="shared-collection-page">
      {/* Header with Store Info */}
      <header className="shared-collection-header">
        <div className="shared-collection-store">
          {store?.logo && (
            <img src={store.logo} alt={store.name} className="store-logo" />
          )}
          <div className="store-info">
            <h3>{store?.name || 'Store'}</h3>
            {storeUrl && (
              <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="visit-store">
                Visit Store
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                </svg>
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Collection Info */}
      <div className="shared-collection-info">
        {(collection.coverImage || collection.image) && (
          <div className="shared-collection-cover">
            <img src={collection.coverImage || collection.image} alt={collection.name} />
            <div className="cover-overlay" />
          </div>
        )}
        <div className="shared-collection-details">
          <h1>{collection.name}</h1>
          {collection.description && (
            <p className="collection-description">{collection.description}</p>
          )}
          <span className="product-count">{products.length} products</span>
        </div>
      </div>

      {/* Products Grid */}
      {products.length === 0 ? (
        <div className="collection-empty">
          <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <p>No products in this collection yet.</p>
        </div>
      ) : (
        <div className="shared-products-grid">
          {products.map((product) => (
            <a
              key={product.id}
              href={storeUrl ? `${storeUrl}/product/${product.slug}` : '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="shared-product-card"
            >
              <div className="product-image">
                {product.primaryImage ? (
                  <img src={product.primaryImage} alt={product.name} />
                ) : (
                  <div className="product-placeholder">
                    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <path d="m3 15 6-6 4 4 8-8" />
                    </svg>
                  </div>
                )}
                {product.compareAtPrice && product.compareAtPrice > product.price && (
                  <span className="sale-badge">Sale</span>
                )}
              </div>
              <div className="product-details">
                <h3 className="product-name">{product.name}</h3>
                <div className="product-pricing">
                  <span className="current-price">{formatPrice(product.price)}</span>
                  {product.compareAtPrice && product.compareAtPrice > product.price && (
                    <span className="compare-price">{formatPrice(product.compareAtPrice)}</span>
                  )}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Footer CTA */}
      {storeUrl && (
        <div className="shared-collection-footer">
          <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="shop-now-btn">
            Shop Now at {store?.name}
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      )}

      {/* Powered By */}
      <div className="powered-by">
        <span>Powered by</span>
        <a href="https://userevstar.com" target="_blank" rel="noopener noreferrer">
          Revstar
        </a>
      </div>
    </div>
  );
};

// Additional CSS for shared collection page
const style = document.createElement('style');
style.textContent = `
.shared-collection-page {
  min-height: 100vh;
  background: #f9fafb;
}

.shared-collection-header {
  background: white;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.shared-collection-store {
  display: flex;
  align-items: center;
  gap: 1rem;
  max-width: 1200px;
  margin: 0 auto;
}

.shared-collection-store .store-logo {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
}

.shared-collection-store .store-info h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem;
}

.shared-collection-store .visit-store {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: #0d7463;
  text-decoration: none;
}

.shared-collection-store .visit-store:hover {
  text-decoration: underline;
}

.shared-collection-info {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
}

.shared-collection-cover {
  position: relative;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1.5rem;
}

.shared-collection-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.shared-collection-cover .cover-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.3));
}

.shared-collection-details h1 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem;
}

.shared-collection-details .collection-description {
  color: #6b7280;
  margin: 0 0 0.75rem;
  line-height: 1.5;
}

.shared-collection-details .product-count {
  font-size: 0.875rem;
  color: #9ca3af;
}

.shared-products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem 2rem;
}

.shared-product-card {
  display: block;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  text-decoration: none;
  transition: all 0.2s ease;
}

.shared-product-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #d1d5db;
}

.shared-product-card .product-image {
  position: relative;
  aspect-ratio: 1;
  background: #f3f4f6;
  overflow: hidden;
}

.shared-product-card .product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.shared-product-card:hover .product-image img {
  transform: scale(1.05);
}

.shared-product-card .product-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
}

.shared-product-card .sale-badge {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
  background: #dc2626;
  color: white;
}

.shared-product-card .product-details {
  padding: 1rem;
}

.shared-product-card .product-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  margin: 0 0 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shared-product-card .product-pricing {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.shared-product-card .current-price {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}

.shared-product-card .compare-price {
  font-size: 0.875rem;
  color: #9ca3af;
  text-decoration: line-through;
}

.shared-collection-footer {
  text-align: center;
  padding: 2rem 1.5rem;
  background: white;
  border-top: 1px solid #e5e7eb;
}

.shop-now-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  background: #0d7463;
  color: white;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.15s ease;
}

.shop-now-btn:hover {
  background: #0a5c4e;
}

.powered-by {
  text-align: center;
  padding: 1.5rem;
  font-size: 0.875rem;
  color: #9ca3af;
}

.powered-by a {
  color: #0d7463;
  font-weight: 500;
  text-decoration: none;
}

.powered-by a:hover {
  text-decoration: underline;
}

.shared-collection-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
  text-align: center;
}

.shared-collection-error svg {
  color: #dc2626;
  margin-bottom: 1rem;
}

.shared-collection-error h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem;
}

.shared-collection-error p {
  color: #6b7280;
  margin: 0;
}

@media (max-width: 640px) {
  .shared-products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .shared-collection-details h1 {
    font-size: 1.25rem;
  }

  .shared-collection-cover {
    height: 150px;
  }
}
`;
document.head.appendChild(style);

export default SharedCollectionPage;
